import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import isEqual from 'lodash.isequal';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getContactsViewKey } from 'helpers/storeKeys';
import { defaultFilters } from '../../../components/organisms/contactsFilters/defaultFilters';
import { trackEvent, trackPage } from '../../../helpers/analytics';
import useContactsFilterCount from '../../../hooks/useContactsFilterCount';
import { activeNetworkSelector } from '../../../redux/selectors';
import { useFetchContacts } from './useFetchContacts';

export const useContactsExtended = ({ allList = true, defaultSort = {}, listKind }) => {
  const { listId, queryParams, setQueryParams, data, customFields, isLoading, isFetching, isLoadingAI } =
    useFetchContacts({
      allList,
      defaultSort,
      listKind,
    });
  const [selectedRows, setSelectedRows] = useState([]);
  const filtersCount = useContactsFilterCount(queryParams.filters);
  const activeNetwork = useSelector(activeNetworkSelector);
  const { meta } = data;
  const listIdToUse = allList ? null : listId;
  const [view] = useLocalStorage(getContactsViewKey(activeNetwork, listId), 'table');

  const isEmpty =
    !isLoading && !data?.ids?.length && meta?.contactsTotal === 0 && isEqual(defaultFilters, queryParams.filters);

  const showAllContactsEmptyState = isEmpty && !listId;
  const showListContactsEmptyState = isEmpty && listId;

  useEffect(() => {
    if (!listId) {
      trackEvent('contacts:view', {
        collectionId: activeNetwork.id,
        total_number_of_contacts: activeNetwork?.contactsCount ?? 0,
        view_mode: view,
        ...(listIdToUse ? { listId: listIdToUse } : undefined),
      });
    }

    trackPage();
  }, [listIdToUse]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadMore = () => {
    if (queryParams.page + 1 <= Math.ceil(data.meta.queryTotal / 20) && !isLoading) {
      setQueryParams({
        ...queryParams,
        page: queryParams.page + 1,
      });
    }
  };

  const onChangeSelectedContact = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows([...selectedRows].filter((i) => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  const onSetFilters = (newFilters) => {
    setQueryParams({ ...queryParams, filters: newFilters, page: 1 });
    setSelectedRows([]);
    window.scrollTo(0, 0);
  };

  return {
    listId,
    queryParams,
    setQueryParams,
    selectedRows,
    setSelectedRows,
    data,
    customFields,
    isLoading,
    isFetching,
    filtersCount,
    meta,
    showAllContactsEmptyState,
    onLoadMore,
    onChangeSelectedContact,
    onSetFilters,
    activeNetwork,
    isLoadingAI,
    showListContactsEmptyState,
  };
};
